import { useEffect, useRef } from "react";
/**
 * `useEffect` that skips runs whose deps still match the mount-time deps.
 *
 * The persistence hooks use this because localStorage is an external store, so
 * syncing to it belongs in an effect — and keeping it out of the setter leaves
 * the state updater pure (StrictMode double-invokes impure updaters in dev).
 * Skipping runs for the mount-time deps means a merely-viewed table never
 * re-freezes its current defaults into storage — only an actual user change is
 * remembered.
 *
 * A boolean "first run" ref is not enough here: the ref survives StrictMode's
 * dev-only setup → cleanup → setup cycle, so the second setup would run the
 * effect with the mount-time value. Comparing deps against the mount-time deps
 * skips both StrictMode invocations; once deps genuinely change, the baseline
 * is cleared so every later run (including a change back to the mount-time
 * value) executes.
 */ export function useEffectAfterMount(effect, deps) {
    const mountDeps = useRef(deps);
    useEffect(function runEffectAfterMount() {
        if (mountDeps.current !== null && depsEqual(mountDeps.current, deps)) {
            return;
        }
        mountDeps.current = null;
        return effect();
    }, deps);
}
function depsEqual(a, b) {
    return a.length === b.length && a.every((dep, i)=>Object.is(dep, b[i]));
}

//# sourceMappingURL=useEffectAfterMount.js.map